import { AfterViewInit, Directive, OnDestroy, Renderer2 } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { LanguageService } from '@igo2/core/language';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Directive({
  selector: '[appFilterableMatSelect]',
  standalone: true
})
export class FilterableMatSelectDirective implements OnDestroy, AfterViewInit {
  private inputElement: HTMLInputElement;
  private destroy$ = new Subject<void>();
  private matOptions: MatOption[];

  constructor(
    private matSelect: MatSelect,
    private languageService: LanguageService,
    private renderer: Renderer2
  ) { }

  ngAfterViewInit(): void {
    this.createFilterInput();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createFilterInput(): void {
    this.inputElement = this.renderer.createElement('input');
    this.renderer.setAttribute(this.inputElement, 'type', 'text');
    this.renderer.setAttribute(this.inputElement, 'placeholder',
      this.languageService.translate.instant('sigt.global.boutons.rechercher'));
    this.renderer.addClass(this.inputElement, 'mat-select-filter-input');

    this.matSelect.openedChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        if (isOpen) {
          this.positionInput();
          this.matOptions = this.matSelect.options.toArray();
          this.filterOptions(''); //show all
        } else {
          if (this.inputElement) {
            this.inputElement.value = '';
          }
        }
      });
  }

  private positionInput() {
    const overlayElement = this.matSelect.panel.nativeElement;
    if (!overlayElement) return;

    // Ensure the input is only added once.
    if (!overlayElement.querySelector('.mat-select-filter-input')) {
      overlayElement.prepend(this.inputElement);

      this.inputElement.focus();

      fromEvent<KeyboardEvent>(this.inputElement, 'keydown')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => {
          if (event.key === ' ') {
            event.stopPropagation();
          }
        });

      fromEvent(this.inputElement, 'input')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: Event) => {
          const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
          this.filterOptions(filterValue);
        });

      fromEvent<KeyboardEvent>(this.inputElement, 'keyup')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => {
          if (event.key === ' ') {
            const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
            this.filterOptions(filterValue);
          }
        });
    }
  }

  private filterOptions(value: string) {
    if (!this.matOptions) {
      return;
    }
    this.matOptions.forEach((option) => {
      const viewValue = option.viewValue.toLowerCase();
      const element = option._getHostElement();
      if (viewValue.includes(value.toLowerCase())) {
        this.renderer.setStyle(element, 'display', 'flex');
      } else {
        this.renderer.setStyle(element, 'display', 'none');
      }
    });
  }
}
